// src/pages/gene.codes/bloods/BloodAnalysiscomponents/HistoricalTrendChart.tsx
import React, { useRef, useEffect, useState, useMemo } from "react";
import { Tag } from "antd";
import { LineChartOutlined } from "@ant-design/icons";
import * as d3 from "d3";

import { BloodTestResult } from "../../../../types/lab/BloodTestTypes";
import { Test } from "../../../../types/healthcare/TestTypes";
import { calculateResultSeverity, parseResultValue, resolveResultRange } from "../../../../utils/geneCodes/bloodResultUtils";
import { RangeContext } from "../../../../utils/geneCodes/referenceRange";
import { analyteIdentity, bloodTestFromOrder, labWallClockDate } from "../../../../utils/geneCodes/bloodTestShape";

interface HistoricalDataPoint {
	date: Date;
	value: number;
	result: BloodTestResult;
	testId: number;
}

// The band that applied to a run of consecutive points; a paediatric→adult crossing starts a new one
interface BandSegment {
	from: number;
	to: number;
	min: number;
	max: number;
}

interface HistoricalTrendChartProps {
	selectedResult: BloodTestResult;
	allTests: Test[];
	rangeContext: RangeContext;
	width?: number;
	height?: number;
	compact?: boolean;
}

// Each point owns the stretch to the midpoints either side of it, so a band change lands between two collections
export const buildBandSegments = (points: { x: number; min: number | null; max: number | null }[], chartWidth: number): BandSegment[] => {
	const segments: BandSegment[] = [];
	points.forEach((point, i) => {
		if (point.min === null || point.max === null) return;
		const from = i === 0 ? 0 : (points[i - 1].x + point.x) / 2;
		const to = i === points.length - 1 ? chartWidth : (point.x + points[i + 1].x) / 2;
		const last = segments[segments.length - 1];
		if (last && last.to === from && last.min === point.min && last.max === point.max) {
			last.to = to;
		} else {
			segments.push({ from, to, min: point.min, max: point.max });
		}
	});
	return segments;
};

const HistoricalTrendChart: React.FC<HistoricalTrendChartProps> = ({ selectedResult, allTests, rangeContext, width = 328, height = 200, compact = false }) => {
	const svgRef = useRef<SVGSVGElement>(null);
	const tooltipRef = useRef<HTMLDivElement>(null);
	const [hoveredPoint, setHoveredPoint] = useState<HistoricalDataPoint | null>(null);

	const getHistoricalData = (selectedResult: BloodTestResult): HistoricalDataPoint[] => {
		if (!selectedResult || !allTests) return [];

		const historicalPoints: HistoricalDataPoint[] = [];

		const wanted = analyteIdentity(selectedResult);
		if (wanted === "name:") return [];

		allTests.forEach((test) => {
			const bloodTestData = bloodTestFromOrder(test);
			if (!bloodTestData?.results) return;

			const matchingResults = bloodTestData.results.filter((r) => analyteIdentity(r) === wanted);

			matchingResults.forEach((result) => {
				// The COLLECTION date is what a trend is plotted against; `reported_at` is
				// the lab's report date and only a fallback when nothing was collected.
				const date = labWallClockDate(result.collected_at || result.reported_at || test.test_date);
				if (!date) return;

				const numericValue = parseResultValue(result);

				if (!isNaN(numericValue)) {
					historicalPoints.push({
						date,
						value: numericValue,
						result: result,
						testId: test.id,
					});
				}
			});
		});

		return historicalPoints.sort((a, b) => a.date.getTime() - b.date.getTime());
	};

	const historicalData = useMemo(() => getHistoricalData(selectedResult), [selectedResult, allTests]);

	useEffect(() => {
		if (!svgRef.current || historicalData.length === 0) return;

		const margin = compact ? { top: 10, right: 10, bottom: 20, left: 30 } : { top: 20, right: 20, bottom: 40, left: 50 };
		const chartWidth = width - margin.left - margin.right;
		const chartHeight = height - margin.top - margin.bottom;

		const svg = d3.select(svgRef.current);
		svg.selectAll("*").remove();

		const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

		// Resolved per point: the patient's age at each collection picks the band, so it can move mid-trend
		const pointRanges = historicalData.map((point) => resolveResultRange(point.result, rangeContext));
		const bandMins = pointRanges.map((r) => r.minValue).filter((v): v is number => v !== null);
		const bandMaxs = pointRanges.map((r) => r.maxValue).filter((v): v is number => v !== null);
		const hasBand = pointRanges.some((r) => r.minValue !== null && r.maxValue !== null);

		const dataMin = d3.min(historicalData, (d) => d.value) || 0;
		const dataMax = d3.max(historicalData, (d) => d.value) || 0;

		let yMin;
		let yMax;

		if (hasBand) {
			const bandMin = Math.min(...bandMins);
			const bandMax = Math.max(...bandMaxs);
			const range = bandMax - bandMin;
			const buffer = range * 0.5;
			yMin = Math.min(dataMin, bandMin - buffer);
			yMax = Math.max(dataMax, bandMax + buffer);
		} else {
			const range = dataMax - dataMin;
			const buffer = range * 0.2;
			yMin = dataMin - buffer;
			yMax = dataMax + buffer;
		}

		const xScale = d3
			.scaleTime()
			.domain(d3.extent(historicalData, (d) => d.date) as [Date, Date])
			.range([0, chartWidth]);

		const yScale = d3.scaleLinear().domain([yMin, yMax]).range([chartHeight, 0]);

		const pointXs = historicalData.map((point) => xScale(point.date));
		const segments = buildBandSegments(
			historicalData.map((_, i) => ({ x: pointXs[i], min: pointRanges[i].minValue, max: pointRanges[i].maxValue })),
			chartWidth,
		);

		segments.forEach(({ from, to, min: minValue, max: maxValue }) => {
			const segmentWidth = to - from;
			const normalRange = maxValue - minValue;

			const mildBufferLow = normalRange * 0.15;
			const moderateBufferLow = normalRange * 0.3;

			const mildBufferHigh = normalRange * 0.15;
			const moderateBufferHigh = normalRange * 0.3;

			const severeLowMin = yMin;
			const severeLowMax = Math.max(yMin, minValue - moderateBufferLow);
			const moderateLowMax = Math.max(yMin, minValue - mildBufferLow);
			const mildLowMax = minValue;

			const mildHighMin = maxValue;
			const moderateHighMin = Math.min(yMax, maxValue + mildBufferHigh);
			const severeHighMin = Math.min(yMax, maxValue + moderateBufferHigh);
			const severeHighMax = yMax;

			if (severeLowMin < severeLowMax) {
				g.append("rect")
					.attr("x", from)
					.attr("y", yScale(severeLowMax))
					.attr("width", segmentWidth)
					.attr("height", yScale(severeLowMin) - yScale(severeLowMax))
					.attr("fill", "#ff4d4f")
					.attr("opacity", 0.15);
			}

			if (severeLowMax < moderateLowMax) {
				g.append("rect")
					.attr("x", from)
					.attr("y", yScale(moderateLowMax))
					.attr("width", segmentWidth)
					.attr("height", yScale(severeLowMax) - yScale(moderateLowMax))
					.attr("fill", "#ff7a45")
					.attr("opacity", 0.15);
			}

			if (moderateLowMax < mildLowMax) {
				g.append("rect")
					.attr("x", from)
					.attr("y", yScale(mildLowMax))
					.attr("width", segmentWidth)
					.attr("height", yScale(moderateLowMax) - yScale(mildLowMax))
					.attr("fill", "#faad14")
					.attr("opacity", 0.15);
			}

			g.append("rect")
				.attr("x", from)
				.attr("y", yScale(maxValue))
				.attr("width", segmentWidth)
				.attr("height", yScale(minValue) - yScale(maxValue))
				.attr("fill", "#52c41a")
				.attr("opacity", 0.15);

			if (mildHighMin < moderateHighMin) {
				g.append("rect")
					.attr("x", from)
					.attr("y", yScale(moderateHighMin))
					.attr("width", segmentWidth)
					.attr("height", yScale(mildHighMin) - yScale(moderateHighMin))
					.attr("fill", "#faad14")
					.attr("opacity", 0.15);
			}

			if (moderateHighMin < severeHighMin) {
				g.append("rect")
					.attr("x", from)
					.attr("y", yScale(severeHighMin))
					.attr("width", segmentWidth)
					.attr("height", yScale(moderateHighMin) - yScale(severeHighMin))
					.attr("fill", "#ff7a45")
					.attr("opacity", 0.15);
			}

			if (severeHighMin < severeHighMax) {
				g.append("rect")
					.attr("x", from)
					.attr("y", yScale(severeHighMax))
					.attr("width", segmentWidth)
					.attr("height", yScale(severeHighMin) - yScale(severeHighMax))
					.attr("fill", "#ff4d4f")
					.attr("opacity", 0.15);
			}

			g.append("line").attr("x1", from).attr("x2", to).attr("y1", yScale(minValue)).attr("y2", yScale(minValue)).attr("stroke", "#52c41a").attr("stroke-width", 1.5).attr("stroke-dasharray", "4,4").attr("opacity", 0.8);

			g.append("line").attr("x1", from).attr("x2", to).attr("y1", yScale(maxValue)).attr("y2", yScale(maxValue)).attr("stroke", "#52c41a").attr("stroke-width", 1.5).attr("stroke-dasharray", "4,4").attr("opacity", 0.8);
		});

		const line = d3
			.line<HistoricalDataPoint>()
			.x((d) => xScale(d.date))
			.y((d) => yScale(d.value))
			.curve(d3.curveMonotoneX);

		g.append("path")
			.datum(historicalData)
			.attr("fill", "none")
			.attr("stroke", "#3b82f6")
			.attr("stroke-width", compact ? 2 : 2.5)
			.attr("d", line);

		historicalData.forEach((point, i) => {
			const severityData = calculateResultSeverity(point.result, rangeContext);

			g.append("circle")
				.attr("cx", pointXs[i])
				.attr("cy", yScale(point.value))
				.attr("r", point.result.id === selectedResult.id ? (compact ? 5 : 7) : compact ? 3 : 5)
				.attr("fill", severityData.color)
				.attr("stroke", "#ffffff")
				.attr("stroke-width", point.result.id === selectedResult.id ? (compact ? 2 : 3) : 2)
				.style("cursor", "pointer")
				.on("mouseover", function () {
					d3.select(this)
						.transition()
						.duration(150)
						.attr("r", compact ? 7 : 9);
					setHoveredPoint(point);

					if (tooltipRef.current) {
						const tooltip = d3.select(tooltipRef.current);
						tooltip.style("opacity", 1);
					}
				})
				.on("mouseout", function () {
					d3.select(this)
						.transition()
						.duration(150)
						.attr("r", point.result.id === selectedResult.id ? (compact ? 5 : 7) : compact ? 3 : 5);
					setHoveredPoint(null);

					if (tooltipRef.current) {
						const tooltip = d3.select(tooltipRef.current);
						tooltip.style("opacity", 0);
					}
				});
		});

		const xAxis = d3
			.axisBottom(xScale)
			.ticks(compact ? 3 : 4)
			.tickFormat(d3.timeFormat("%b %d") as any);

		const yAxis = d3.axisLeft(yScale).ticks(compact ? 4 : 6);

		g.append("g")
			.attr("transform", `translate(0,${chartHeight})`)
			.call(xAxis)
			.attr("class", "x-axis")
			.selectAll("text")
			.style("font-size", compact ? "8px" : "10px")
			.style("fill", "#6b7280");

		g.append("g")
			.call(yAxis)
			.attr("class", "y-axis")
			.selectAll("text")
			.style("font-size", compact ? "8px" : "10px")
			.style("fill", "#6b7280");

		g.selectAll(".x-axis line, .y-axis line, .x-axis path, .y-axis path").style("stroke", "#d1d5db").style("stroke-width", 1);
	}, [historicalData, selectedResult, rangeContext, width, height, compact]);

	if (historicalData.length === 0) {
		return (
			<div style={{ padding: compact ? "8px" : "16px", textAlign: "center", fontSize: compact ? "9px" : "11px", opacity: 0.6 }}>
				<LineChartOutlined style={{ fontSize: compact ? "16px" : "24px", marginBottom: compact ? "4px" : "8px" }} />
				<div>No historical data</div>
			</div>
		);
	}

	return (
		<div style={{ position: "relative" }}>
			{!compact && (
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
					<span style={{ fontSize: "11px", fontWeight: 500 }}>TREND OVER TIME</span>
					<Tag style={{ fontSize: "10px" }}>{historicalData.length} tests</Tag>
				</div>
			)}
			<svg ref={svgRef} width={width} height={height} style={{ display: "block" }} />
			<div
				ref={tooltipRef}
				style={{
					position: "absolute",
					opacity: 0,
					pointerEvents: "none",
					background: "rgba(0, 0, 0, 0.85)",
					color: "white",
					padding: "8px 12px",
					borderRadius: "4px",
					fontSize: "11px",
					zIndex: 1000,
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					whiteSpace: "nowrap",
				}}
			>
				{hoveredPoint && (
					<div>
						<div style={{ fontWeight: 600, marginBottom: "4px" }}>
							{hoveredPoint.value} {selectedResult.units}
						</div>
						<div style={{ opacity: 0.9 }}>{hoveredPoint.date.toLocaleDateString()}</div>
						{calculateResultSeverity(hoveredPoint.result, rangeContext).isAbnormal && (
							<div style={{ marginTop: "4px" }}>
								<Tag color={calculateResultSeverity(hoveredPoint.result, rangeContext).tagColor} style={{ fontSize: "10px" }}>
									{calculateResultSeverity(hoveredPoint.result, rangeContext).label}
								</Tag>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default HistoricalTrendChart;
